import { useEffect, useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { Check, Stars02 } from "@untitledui/icons"
import { cx } from "@/lib/utils/cx"
import { FE_ORCHESTRATOR_COPY } from "./copy"

// Transient loader shown after the Damage editor's Next while the
// sections-orchestrator job runs. Picks up the 2 stages the damage
// agent didn't cover (repair-vs-replace + pricing) so the user gets
// continuity with the Generate screen's choreography.
//
// Visually lighter than StepProcessing — no silhouette card, no
// vehicle recap. The user already saw all that in the Generate step;
// this is just "we're crunching numbers, hold on."

interface OrchestratorLoaderProps {
  /** Drives whether the visual stays "running" or flips to "done".
   *  Pass false while the orchestrator job is in flight; true after
   *  ping returns finishedOn. The component handles its own minimum
   *  duration so a fast BE doesn't make the loader flash. */
  done: boolean
}

// Each stage holds for ~8s so the user has time to read it; the
// orchestrator typically takes ~15s, so the two stages cover that
// window without finishing early. `ready` still gates on the BE
// poll, so this is a minimum — never a fixed — choreography time.
const STAGE_DURATION_MS = 8000
const TICKER_INTERVAL_MS = 2400

export function OrchestratorLoader({ done }: OrchestratorLoaderProps) {
  const [activeStageIdx, setActiveStageIdx] = useState(0)
  const [stagesDone, setStagesDone] = useState(false)
  const [tickerIdx, setTickerIdx] = useState(0)

  // Choreography — advance every STAGE_DURATION_MS until last stage,
  // then sit on it until the BE actually finishes.
  useEffect(() => {
    if (stagesDone) return
    if (activeStageIdx >= FE_ORCHESTRATOR_COPY.stages.length - 1) {
      const t = window.setTimeout(() => setStagesDone(true), STAGE_DURATION_MS)
      return () => window.clearTimeout(t)
    }
    const t = window.setTimeout(
      () => setActiveStageIdx((i) => i + 1),
      STAGE_DURATION_MS,
    )
    return () => window.clearTimeout(t)
  }, [activeStageIdx, stagesDone])

  // Activity ticker — rotates messages within the active stage.
  useEffect(() => {
    if (done) return
    setTickerIdx(0)
    const t = window.setInterval(
      () => setTickerIdx((i) => i + 1),
      TICKER_INTERVAL_MS,
    )
    return () => window.clearInterval(t)
  }, [activeStageIdx, done])

  const ready = stagesDone && done
  const activeStage = FE_ORCHESTRATOR_COPY.stages[activeStageIdx]
  const tickerMessage =
    activeStage.tickers[tickerIdx % activeStage.tickers.length]

  return (
    <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden">
      <div className="p-6 md:p-10 space-y-5">
        <header>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-emerald-600 mb-2">
            {ready ? FE_ORCHESTRATOR_COPY.ready : FE_ORCHESTRATOR_COPY.thinking}
          </p>
          <h2 className="text-xl md:text-2xl font-bold text-zinc-900 tracking-tight leading-tight">
            {FE_ORCHESTRATOR_COPY.title}
          </h2>
          <p className="mt-1.5 text-[13.5px] text-zinc-500 leading-relaxed">
            {FE_ORCHESTRATOR_COPY.subtitle}
          </p>
        </header>

        <ol className="space-y-1.5" aria-live="polite">
          {FE_ORCHESTRATOR_COPY.stages.map((stage, idx) => {
            const status: "pending" | "active" | "done" = stagesDone
              ? "done"
              : idx < activeStageIdx
                ? "done"
                : idx === activeStageIdx
                  ? "active"
                  : "pending"
            return (
              <li key={stage.id}>
                <StageRow label={stage.label} status={status} />
              </li>
            )
          })}
        </ol>

        <AnimatePresence mode="wait" initial={false}>
          {ready ? (
            <motion.div
              key="ready"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
              className="w-full h-12 rounded-2xl flex items-center justify-center gap-2 bg-emerald-50 ring-1 ring-emerald-200 text-emerald-900 text-[13px] font-semibold"
            >
              <Check className="size-4 text-emerald-600" strokeWidth={2.6} aria-hidden />
              {FE_ORCHESTRATOR_COPY.ready}
            </motion.div>
          ) : (
            <motion.div
              key="working"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full h-12 rounded-2xl bg-zinc-100 text-zinc-500 flex items-center justify-center gap-2 text-[12.5px] font-medium"
            >
              <Stars02 className="size-4 animate-pulse text-indigo-500" aria-hidden />
              {stagesDone ? FE_ORCHESTRATOR_COPY.syncing : tickerMessage}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

function StageRow({
  label,
  status,
}: {
  label: string
  status: "pending" | "active" | "done"
}) {
  return (
    <div
      className={cx(
        "relative rounded-xl px-3.5 py-2.5 flex items-center gap-3 overflow-hidden transition-colors duration-200",
        status === "active"
          ? "bg-indigo-50/70 border border-indigo-100"
          : status === "done"
            ? "bg-emerald-50/40 border border-emerald-100"
            : "bg-zinc-50 border border-zinc-100",
      )}
    >
      {status === "active" && (
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-100/0 via-indigo-200/40 to-indigo-100/0"
          initial={{ x: "-30%" }}
          animate={{ x: "130%" }}
          transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
          style={{ width: "30%" }}
          aria-hidden
        />
      )}
      <span
        className={cx(
          "relative size-6 rounded-full grid place-items-center shrink-0 transition-colors duration-200",
          status === "active"
            ? "bg-indigo-500 text-white shadow-sm"
            : status === "done"
              ? "bg-emerald-500 text-white"
              : "bg-zinc-200 text-zinc-400",
        )}
        aria-hidden
      >
        {status === "done" ? (
          <motion.svg
            viewBox="0 0 16 16"
            className="size-3"
            fill="none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2 }}
          >
            <motion.path
              d="M3 8 l3 3 7-7"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ duration: 0.35, ease: "easeOut" }}
            />
          </motion.svg>
        ) : status === "active" ? (
          <motion.span
            className="size-1.5 rounded-full bg-white"
            animate={{ scale: [0.8, 1.25, 0.8] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
          />
        ) : (
          <span className="size-1.5 rounded-full bg-zinc-400" />
        )}
      </span>
      <span
        className={cx(
          "relative text-[13px] font-semibold leading-tight",
          status === "active"
            ? "text-indigo-900"
            : status === "done"
              ? "text-emerald-900"
              : "text-zinc-500",
        )}
      >
        {label}
      </span>
      {status === "done" && (
        <Check
          className="relative size-4 text-emerald-600 ml-auto shrink-0"
          strokeWidth={2.5}
          aria-hidden
        />
      )}
    </div>
  )
}
